/* eslint-disable jira/filename-case */
import React, { memo, useCallback, useMemo, type PropsWithChildren } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Inline } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import {
	FireUiAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
	SCREEN,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { AstContainer, useAnonymizedJql, useHasJql } from '../controllers/ast/index.tsx';
import type { ContainerProps } from '../controllers/ast/types.tsx';
import { useNumberOfSelectedFields } from '../controllers/ast/use-number-of-selected-fields/index.tsx';
import { useNumberOfSelectedValues } from '../controllers/ast/use-selected-values/index.tsx';
import { SOURCE_NAME, TEAM_NAME, PACKAGE_NAME } from '../common/constants.tsx';
import { OnJQLChangeContext, OnJQLChangePubSub } from '../controllers/on-jql-change/index.tsx';
import type { FilterRefinement$key } from './__generated__/FilterRefinement.graphql';
import { AssigneePicker } from './assignee-picker/AssigneePicker.tsx';
import { FilterPopup } from './filter-popup/index.tsx';
import SearchField from './search-field/index.tsx';
import type { Props } from './types.tsx';
import ErrorComponent from './error/index.tsx';

const OnJQLChangeProvider = componentWithCondition(
	() => fg('list_lovability_improving_filters'),
	OnJQLChangeContext.Provider,
	(props: PropsWithChildren<{}>) => props.children,
);

const FilterRefinementWithErrorBoundary = ({
	debounce = 500,
	defaultJql,
	onSearch,
	...props
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onJQLChangePubSub = useMemo(() => {
		if (fg('list_lovability_improving_filters')) {
			return new OnJQLChangePubSub();
		}
		return null;
	}, []);

	const handleOnSearch = useCallback<Required<ContainerProps>['onJqlChange']>(
		(updateJql: string, fieldType?: string, amountOfSelectedOptionOfFieldType?: number) => {
			onSearch && onSearch(updateJql);
			fg('list_lovability_improving_filters') && onJQLChangePubSub?.publish(updateJql);
			fireUIAnalytics(createAnalyticsEvent({}), {
				action: 'changed',
				actionSubject: 'filterRefinement',
				actionSubjectId: 'changedFilterRefinement',
				attributes: {
					source: SOURCE_NAME,
					fieldType,
					amountOfSelectedOptionsOfFieldType: amountOfSelectedOptionOfFieldType,
				},
			});
		},
		[onSearch, onJQLChangePubSub, createAnalyticsEvent],
	);

	const [handleSearchDebounced] = useDebouncedCallback(handleOnSearch, debounce);

	return (
		<JSErrorBoundary
			id={SOURCE_NAME}
			packageName={PACKAGE_NAME}
			teamName={TEAM_NAME}
			sendToPrivacyUnsafeSplunk
			fallback={() => <ErrorComponent />}
		>
			<OnJQLChangeProvider value={onJQLChangePubSub}>
				<AstContainer
					defaultJql={defaultJql}
					onJqlChange={handleSearchDebounced}
					isModeSwitcherEnabled={
						fg('list_lovability_improving_filters') ? props.isModeSwitcherEnabled : false
					}
				>
					<FilterRefinement {...props} />
				</AstContainer>
			</OnJQLChangeProvider>
		</JSErrorBoundary>
	);
};

const FilterRefinement = ({
	enablePostOfficeTour = false,
	changeBoardingView,
	fragmentRef,
	isAddPeopleButtonEnabled,
	projectId,
	projectKey,
	projectName,
	scope,
	searchFieldPlaceholder,
	showAssigneeFilter = true,
	changeboardingMessageId,
	isSoftwareFullTheming,
	isResponsive = true,
	isModeSwitcherEnabled = false,
	projectContext,
	onReset,
	jqlMessages,
}: Omit<Props, 'defaultJql' | 'debounce' | 'onSearch'>) => {
	const data = useFragment<FilterRefinement$key>(
		graphql`
			fragment FilterRefinement on JiraQuery
			@argumentDefinitions(
				assignees: { type: "Int", defaultValue: 100 }
				cloudId: { type: "ID!" }
				scope: { type: "JiraJqlScopeInput" }
			) {
				...AssigneePicker @arguments(assignees: $assignees, cloudId: $cloudId, scope: $scope)
			}
		`,
		fragmentRef,
	);

	const numberOfSelectedValues = useNumberOfSelectedValues(
		fg('list_lovability_improving_filters') ? projectContext : undefined,
	);
	const anonymizedJql = useAnonymizedJql();
	const hasJql = useHasJql();
	const numberOfSelectedFields = useNumberOfSelectedFields();

	const analyticsAttributes = useMemo(
		() => ({
			isAssigneeFilterRendered: showAssigneeFilter,
			numberOfSelectedValues,
			changeboardingMessageId,
			anonymizedJql,
			hasJql,
			numberOfSelectedFields,
			...(fg('list_lovability_improving_filters') ? { isModeSwitcherEnabled } : {}),
		}),
		[
			showAssigneeFilter,
			numberOfSelectedValues,
			changeboardingMessageId,
			anonymizedJql,
			hasJql,
			numberOfSelectedFields,
			isModeSwitcherEnabled,
		],
	);

	return (
		<UFOSegment name="filter-refinement">
			<ContextualAnalyticsData
				sourceName={SOURCE_NAME}
				sourceType={SCREEN}
				attributes={analyticsAttributes}
			>
				<Inline alignBlock="center" space="space.075">
					<SearchField inputLabel={searchFieldPlaceholder} />
					{showAssigneeFilter && (
						<AssigneePicker
							fragmentRef={data}
							isAddPeopleButtonEnabled={isAddPeopleButtonEnabled}
							projectKey={projectKey}
							projectId={projectId}
							projectName={projectName}
							isResponsive={isResponsive}
							{...(fg('list_lovability_improving_filters') && {
								isModeSwitcherEnabled,
								jqlMessages,
							})}
						/>
					)}
					<FilterPopup
						scope={scope}
						changeboardingMessageId={changeboardingMessageId}
						enablePostOfficeTour={enablePostOfficeTour}
						changeBoardingView={changeBoardingView}
						isSoftwareFullTheming={isSoftwareFullTheming}
						{...(fg('list_lovability_improving_filters') && {
							isModeSwitcherEnabled,
							projectContext,
							jqlMessages,
							onReset,
						})}
					/>
				</Inline>
				<FireUiAnalytics
					actionSubject="screen"
					action="viewed"
					actionSubjectId="filterRefinement"
				/>
			</ContextualAnalyticsData>
		</UFOSegment>
	);
};

export default memo(FilterRefinementWithErrorBoundary);
